import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));
  const category = article ? categories.find((c) => c.id === article.categoryId) : null;
  const relatedArticles = article
    ? articles
        .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
        .slice(0, 3)
    : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="font-heading text-2xl text-foreground-900 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    url: `https://mitaowang.com/article/${article.id}`,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '蜜桃网',
    },
    keywords: article.tags.join(','),
    wordCount: article.content.length,
    timeRequired: `PT${article.readTime}M`,
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `关于${article.title}这篇文章的核心内容是什么？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: article.excerpt,
        },
      },
      {
        '@type': 'Question',
        name: `蜜桃网如何保证${category?.name || '分类'}内容的品质？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蜜桃网编辑团队严格审核每一篇内容，确保信息的准确性、时效性和可读性，为用户提供高品质的阅读体验。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(articleJsonLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqJsonLd)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Article Header */}
        <article className="pt-24 pb-8 md:pt-32 md:pb-12">
          <div className="w-full max-w-4xl mx-auto px-6 md:px-10">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                  { label: article.title },
                ]}
              />
            </div>

            <div className="mt-8">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full mb-4 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${category.icon} text-sm`}></i>
                  {category.name}
                </Link>
              )}
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-6 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-primary-600">{article.author[0]}</span>
                  </div>
                  <span className="text-foreground-700 font-medium">{article.author}</span>
                </div>
                <span className="text-foreground-300">|</span>
                <span>{article.date}</span>
                <span className="text-foreground-300">|</span>
                <span>{article.readTime} 分钟阅读</span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="w-full rounded-2xl overflow-hidden mb-10">
              <div className="w-full h-64 md:h-96">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  title={`${article.title} - 蜜桃网`}
                />
              </div>
            </div>

            {/* Article Content */}
            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500 mr-2">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-10 text-center">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    id={ra.id}
                    title={ra.title}
                    excerpt={ra.excerpt}
                    categoryId={ra.categoryId}
                    author={ra.author}
                    date={ra.date}
                    image={ra.image}
                    readTime={ra.readTime}
                    variant="default"
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}